import React, { createRef, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import Icon from '../IconBase/Icon';
import Slider from 'react-slick';
import useIsMobile from '../../Hooks/useIsMobile';
import SliderThumbnails from '../SliderThumbnails/SliderThumbnails';
import(`./styles/${window.__litium.themeName}.scss`);

const FullscreenImageSlider = ({
  showCloseButton = true,
  images,
  initialSlide = 0,
  onClosed,
  showThumbnails = true,
  infinite = true,
} = props) => {
  const dispatch = useDispatch();
  const contentRef = createRef();
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [zoomClickPos, setZoomClickPos] = useState(0);
  const [visible, setVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [isSliding, setIsSliding] = useState(false);
  const [doubleClickMode, setDoubleClickMode] = useState(
    images.map(() => 'zoomIn')
  );
  const [swipingEnabled, setSwipingEnabled] = useState(true);
  const [transformComponentRefs] = useState(images.map(() => createRef()));
  const sliderRef = useRef();

  // Fixes issue in slick slider with initial slide
  const [hasSetPosition, setHasSetPosition] = useState(false);
  useEffect(() => {
    if (sliderRef.current && !hasSetPosition) {
      sliderRef.current?.slickGoTo(currentSlide);
      setHasSetPosition(true);
    }
  }, [currentSlide, hasSetPosition, sliderRef]);

  const next = () => {
    sliderRef.current.slickNext();
  };
  const prev = () => {
    sliderRef.current.slickPrev();
  };

  const settings = {
    lazyLoad: true,
    infinite: infinite,
    speed: 200,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    swipe: swipingEnabled,
    initialSlide: initialSlide,
    beforeChange: (_, index) => {
      setCurrentSlide(index);
      transformComponentRefs.forEach((ref) => ref?.current?.resetTransform());
      setSwipingEnabled(true);
    },
    onSwipe: () => setIsSliding(true),
    afterChange: () => setIsSliding(false),
  };

  const onKeyDown = (e) => {
    if (e.key == 'Escape') {
      setOpen(false);
    }
  };

  /**
   * Set "open" to true on mount
   */
  useEffect(() => {
    setVisible(true);
    dispatch(
      addOverlay('no-scroll', () => {
        setOpen(false);
      })
    );
    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      dispatch(removeOverlay('no-scroll'));
      onClosed && onClosed();
    }
  }, [open]);

  const onZoom = (e) => {
    if (isSliding) return;

    e.stopPropagation();
    const didZoomIn = !zoomed;
    const clickPos = e.pageY;
    setZoomClickPos(didZoomIn ? clickPos : 0);
    setZoomed(didZoomIn);
  };

  useEffect(() => {
    if (zoomed) {
      const windowHeight = contentRef.current.getBoundingClientRect().height;
      const percentage = zoomClickPos / windowHeight;
      const upScaledClickPos = contentRef.current.scrollHeight * percentage;
      const centeredClickPos = upScaledClickPos - windowHeight / 2;
      contentRef.current.scrollTo({ top: centeredClickPos, behavior: 'auto' });
    }
  }, [zoomClickPos]);

  const onMobileZoom = (e, idx) => {
    setTimeout(() => {
      let doubleClickModeCopy = Object.assign([], doubleClickMode);
      doubleClickModeCopy[idx] =
        Math.round(e.state.scale) >= 8 ? 'reset' : 'zoomIn';

      setDoubleClickMode(doubleClickModeCopy);
      setSwipingEnabled(e.state.scale <= 1);
    }, 200);
  };

  return (
    open &&
    ReactDOM.createPortal(
      <div
        className={`fullscreen-image-slider ${
          visible && 'fullscreen-image-slider--visible'
        } ${zoomed ? 'zoomed' : ''}`}
      >
        <div className="fullscreen-image-slider__content" ref={contentRef}>
          {showCloseButton && (
            <div
              className="fullscreen-image-slider__close-btn"
              onClick={() => setOpen(false)}
            >
              <Icon
                className="fullscreen-image-slider__close-btn__icon"
                iconCode="close"
              />
            </div>
          )}
          <Slider {...settings} ref={sliderRef} touchThreshold={20}>
            {images.map((img, idx) => (
              <div key={'fullwidth-image-slide' + idx}>
                {/* Use "zoom" library on mobile */}
                {isMobile && (
                  <TransformWrapper
                    ref={transformComponentRefs[idx]}
                    centerOnInit={true}
                    wheel={{ wheelDisabled: true }}
                    panning={{
                      disabled: swipingEnabled,
                      velocityDisabled: true,
                    }}
                    limitToBounds={true}
                    velocityAnimation={{ disabled: true }}
                    doubleClick={{ mode: doubleClickMode[idx] }}
                    onPanningStop={(e) => onMobileZoom(e, idx)}
                    onZoomStop={(e) => onMobileZoom(e, idx)}
                  >
                    <TransformComponent
                      wrapperClass="fullscreen-image-slider__transform-wrapper"
                      contentClass="fullscreen-image-slider__transform-component"
                    >
                      <img
                        src={img.originalUrl}
                        loading={idx == 0 ? 'eager' : 'lazy'}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                )}

                {/* Use zoom-on-click in desktop*/}
                {!isMobile && <img src={img.originalUrl} onClick={onZoom} />}
              </div>
            ))}
          </Slider>
          {showThumbnails && (
            <div className="fullscreen-image-slider__thumbnails-container">
              <div
                className={`fullscreen-image-slider__arrow ${
                  currentSlide < 1
                    ? 'fullscreen-image-slider__arrow--disabled'
                    : ''
                }`}
              >
                <Icon
                  className="fullscreen-image-slider__arrow__icon"
                  iconCode="chevron_left"
                  onClick={prev}
                />
              </div>
              <div className="fullscreen-image-slider__count">
                {currentSlide + 1} / {images.length}
              </div>
              <SliderThumbnails
                className="fullscreen-image-slider__thumbnails"
                images={images}
                sliderRef={sliderRef}
                activeSlide={currentSlide}
              />
              <div
                className={`fullscreen-image-slider__arrow ${
                  currentSlide >= images.length - 1
                    ? 'fullscreen-image-slider__arrow--disabled'
                    : ''
                }`}
              >
                <Icon
                  className="fullscreen-image-slider__arrow__icon"
                  iconCode="chevron_right"
                  onClick={next}
                />
              </div>
            </div>
          )}
        </div>
      </div>,
      document.body
    )
  );
};

export default FullscreenImageSlider;
